import { Artist, OrderPaymentStatus, OrderStatus } from '@favid-inc/api';
import { WhereElement } from '@favid-inc/api/lib/rest';
import { Filter } from 'core/generic';
import { fetchAllArtists } from 'features/artist';
import _ from 'lodash';
import React from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { Accordion, DateRange, LoadingField } from 'utils';

interface State {
  startFulfilledDate?: Date;
  endFulfilledDate?: Date;
  startPlacedDate?: Date;
  endPlacedDate?: Date;
  startDeclinedDate?: Date;
  endDeclinedDate?: Date;
  status?: OrderStatus;
  paymentStatus?: OrderPaymentStatus;
  accordion: boolean[];
  id?: number;
  artistId?: number;
  artist: {
    isLoading: boolean;
    list: Artist[];
  };
}

interface Props {
  onFilterChange: (filter: WhereElement[]) => void;
  filter: Filter;
  isArtistSearchDisable?: boolean;
}

const Style = styled.div`
  .react-datepicker-wrapper {
    display: block;
    > .react-datepicker__input-container {
      display: block;
    }
  }
`;

const Icon = styled.i`
  float: right;
  cursor: pointer;
  margin: 0 !important;
`;

const prefix = /^start|end/;

const unwrapValue = (attr: string, where?: WhereElement[]) => {
  const key = attr.replace(prefix, 'status');
  const exp: WhereElement['exp'] = attr.startsWith('start') ? '>=' : '<=';
  const index = _.findIndex(where, { attr: key, exp });
  if (where && where[index]) {
    return new Date(where[index].value);
  }
};

const unwrapSelectValue = (attr: string, where?: WhereElement[]) => {
  const index = _.findIndex(where, { attr });
  if (where && where[index]) {
    return where[index].value;
  }
};

class FilterComponentWrap extends React.Component<Props> {
  state: State = {
    startFulfilledDate: unwrapValue(
      'startFulfilledDate',
      this.props.filter.where,
    ),
    startDeclinedDate: unwrapValue(
      'startDeclinedDate',
      this.props.filter.where,
    ),
    startPlacedDate: unwrapValue('startPlacedDate', this.props.filter.where),
    endFulfilledDate: unwrapValue('endFulfilledDate', this.props.filter.where),
    endDeclinedDate: unwrapValue('endDeclinedDate', this.props.filter.where),
    endPlacedDate: unwrapValue('endPlacedDate', this.props.filter.where),
    status: unwrapSelectValue('status', this.props.filter.where),
    paymentStatus: unwrapSelectValue('paymentStatus', this.props.filter.where),
    accordion: [false, false, false, false, false],
    id: unwrapSelectValue('id', this.props.filter.where),
    artistId: unwrapSelectValue('artistId', this.props.filter.where),
    artist: {
      isLoading: true,
      list: [],
    },
  };

  async componentDidMount() {
    const list = await fetchAllArtists();
    this.setState({ artist: { list, isLoading: false } });
  }

  cleanFilters = (e: React.FormEvent) => {
    e.preventDefault();
    this.props.onFilterChange([]);
  };

  onFilter = (e: React.FormEvent) => {
    e.preventDefault();

    const dateFilters = Object.entries(this.state).filter(
      ([attr, value]) => prefix.test(attr) && value,
    );
    const filter: WhereElement[] = dateFilters.map(([attr, value]) => {
      const exp = attr.startsWith('start') ? '>=' : '<=';
      value = new Date(value).getTime();
      attr = attr.replace(prefix, 'status');
      return { attr, exp, value };
    });

    const selectFilters = ['id', 'artistId', 'status', 'paymentStatus'];
    Object.entries(this.state)
      .filter(([attr, value]) => selectFilters.includes(attr) && value)
      .forEach(([attr, value]) =>
        filter.push({
          attr,
          exp: '=',
          value,
        }),
      );

    this.props.onFilterChange(filter);
  };

  handleChange = (date: Date, event: React.SyntheticEvent<HTMLElement>) => {
    const name = event.currentTarget.className
      .split(/\s/)
      .filter((item) => !item.startsWith('react'))[0];
    this.setState({ [name]: date });
  };

  onSelectChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const { name, value } = event.currentTarget;
    this.setState({ [name]: value || undefined });
  };

  onAccordeonChange = (event: React.MouseEvent<HTMLElement>) => {
    const key = Number(event.currentTarget.dataset.value);
    const { accordion } = this.state;
    accordion[key] = !accordion[key];
    this.setState({ accordion });
  };

  onNumericInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    if (!isNaN(Number(value))) {
      this.setState({ [name]: value });
    }
  };

  onChangeArtist = (params: any) => {
    this.setState({ artistId: params.value });
  };

  onChangeAllAccordions = () => {
    const { accordion } = this.state;
    const isAllExpanded = accordion.every((item) => item);
    const updatedAccordion = accordion.map(() =>
      isAllExpanded ? false : true,
    );
    this.setState({ accordion: updatedAccordion });
  };

  formatSelectKey(key: string) {
    switch (key) {
      default:
        return _.camelCase(key);
    }
  }

  renderTextFilters() {
    const index = 0;
    const isActive = this.state.accordion[index];

    const ArtistField = () => {
      const { list, isLoading } = this.state.artist;
      if (isLoading) {
        return <LoadingField label='Artista' />;
      }

      const artist = _.find(list, { id: this.state.artistId });
      const selected = artist ? { value: artist.id, label: artist.name } : null;
      const options = list.map(({ id, name }) => ({ value: id, label: name }));

      return (
        <div className='field'>
          <label>Artista</label>
          <Select
            isDisabled={this.props.isArtistSearchDisable}
            value={selected}
            options={options}
            onChange={this.onChangeArtist}
          />
        </div>
      );
    };

    return (
      <Accordion
        target={String(index)}
        isActive={isActive}
        title='Artista e pedido'
        handleClick={this.onAccordeonChange}
      >
        <div className='two fields'>
          <div className='field'>
            <label>ID do pedido</label>
            <input
              name='id'
              value={this.state.id || ''}
              onChange={this.onNumericInputChange}
            />
          </div>
          <ArtistField />
        </div>
      </Accordion>
    );
  }

  renderOptions(type: {}) {
    return Object.entries(type).map(([key, value]) => (
      <option key={key} value={value as string}>
        {this.formatSelectKey(key)}
      </option>
    ));
  }

  renderSelectFilters() {
    const index = 1;
    const isActive = this.state.accordion[index];
    const { status, paymentStatus } = this.state;
    return (
      <Accordion
        target={String(index)}
        isActive={isActive}
        title='Status do pedido e pagamento'
        handleClick={this.onAccordeonChange}
      >
        <div className='two fields'>
          <div className='field'>
            <label>Status do pedido</label>
            <select
              className='ui fluid dropdown'
              value={status}
              name='status'
              onChange={this.onSelectChange}
            >
              <option value={''}>Todos</option>
              {this.renderOptions(OrderStatus)}
            </select>
          </div>
          <div className='field'>
            <label>Status do pagamento</label>
            <select
              className='ui fluid dropdown'
              value={paymentStatus}
              name='paymentStatus'
              onChange={this.onSelectChange}
            >
              <option value={''}>Todos</option>
              {this.renderOptions(OrderPaymentStatus)}
            </select>
          </div>
        </div>
      </Accordion>
    );
  }

  renderDateFilters() {
    const accordeons = [
      {
        key: 'FulfilledDate',
        title: 'Data de envio pelo artista',
        startDate: this.state.startFulfilledDate,
        endDate: this.state.endFulfilledDate,
        accordionIndex: 2,
      },
      {
        key: 'DeclinedDate',
        title: 'Data de negação do pedido',
        startDate: this.state.startDeclinedDate,
        endDate: this.state.endDeclinedDate,
        accordionIndex: 3,
      },
      {
        key: 'PlacedDate',
        title: 'Data de envio pelo cliente',
        startDate: this.state.startPlacedDate,
        endDate: this.state.endPlacedDate,
        accordionIndex: 4,
      },
    ];

    return this.state.accordion.map((isActive, index) => {
      const accordeon = _.find(
        accordeons,
        ({ accordionIndex }) => accordionIndex === index,
      );
      if (!accordeon) {
        return null;
      }

      const { title, startDate, endDate, key } = accordeon;
      return (
        <Accordion
          key={`accordion_${index}`}
          target={String(index)}
          isActive={isActive}
          title={title}
          handleClick={this.onAccordeonChange}
        >
          <DateRange
            stateKey={key}
            startDate={startDate}
            endDate={endDate}
            onChange={this.handleChange}
          />
        </Accordion>
      );
    });
  }

  renderActions() {
    return (
      <div style={{ textAlign: 'center', padding: '1em 0' }}>
        <button className='ui button' onClick={this.cleanFilters}>
          Limpar filtros
        </button>
        <button
          type='submit'
          className='ui primary button'
          onClick={this.onFilter}
        >
          Filtrar
        </button>
      </div>
    );
  }

  render() {
    const isAllExpanded = this.state.accordion.every((item) => item);
    const style = `icon square outline ${isAllExpanded ? 'minus' : 'plus'}`;

    return (
      <form className='ui form'>
        <Style>
          <h4 className='ui dividing header'>
            Filtros
            <Icon className={style} onClick={this.onChangeAllAccordions} />
          </h4>
          {this.renderTextFilters()}
          {this.renderSelectFilters()}
          {this.renderDateFilters()}
          {this.renderActions()}
        </Style>
      </form>
    );
  }
}

export const FilterComponent = FilterComponentWrap;
